import { useContext, useState } from "react";
import { useEffect } from "react";
import { useParams } from "react-router";

import SimpleModal from "../../../../../shared/components/simple-modal/simple-modal";
import AuthContext from "../../../../../shared/context/auth-context";
import useHttp from "../../../../../shared/hooks/use-http";
import { getAllData, postSingle } from "../../../../../shared/utils/api";
import { createUUID, validateLoadingStatusNew } from "../../../../../shared/utils/utils";
import { noop } from "../../../../utils";
import ControlledTable from "../../../controlled-table/controlled-table";
import { IColumn, ISortedBy, ITableRowData } from "../../../data-table/data-table";
import ReferenceWidget from "../reference-widget/reference-widget";
import Spinner from "@/shared/components/spinner/spinner";

export interface IRelatedEntityWidget {
  id: string;
  type: "relatedEntity";
  modalType: "datamodelRelatedEntity"
  label: string;
  selectedColumns: IColumn[];
  datamodelId: string;
  modalData?: { // Extra information the modal needs to edit this widget
    [index: string]: string;
  }
}

interface IRelatedEntityWidgetProps {
  widget: IRelatedEntityWidget;
  previewMode?: boolean;
}

const RelatedEntityWidget = ({widget, previewMode}: IRelatedEntityWidgetProps) => {
  const authCtx = useContext(AuthContext);

  const params = useParams();
  const [data, setData] = useState<ITableRowData[]>([]);
  const [sortedByCol, setSortedByCol] = useState<ISortedBy>({column: widget.selectedColumns[0].key, direction: "asc"});
  const [showNewModal, setShowNewModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isUserReadOnly, setIsUserReadOnly] = useState(false);

  const { sendRequest: getTableData, status: getTableDataStatus, data: getTableDataResult, error: getTableDataError } = useHttp(getAllData, false, authCtx.token, "relatedEntity", "manager");
  const { sendRequest: postRelatedEntityData, status: postRelatedEntityDataStatus, data: postRelatedEntityDataResult, error: postRelatedEntityDataError } = useHttp(postSingle, false, authCtx.token, "relatedEntity", "manager");

  useEffect(() => {
    const roles = authCtx.selectedTenant.roles || [];
    setIsUserReadOnly(roles.length === 1 && roles[0] === "Read");
  }, [authCtx.selectedTenant]);
  
  useEffect(() => {
    if (previewMode) {
      const dummyData: ITableRowData[] = [];
      for (let i = 0; i < 2; i++) {
        dummyData.push(
          widget.selectedColumns.reduce((acc, curr) => {
            acc[curr.key] = "...";
            return acc;
          }, {id: i.toString()} as ITableRowData)
        );
      }
      setData(dummyData);
      return;
    }
    getTableData(`alertcase/${params.id}/${widget.datamodelId}/related_entities?order_by=${sortedByCol.column} ${sortedByCol.direction}`);
  }, [sortedByCol]);
  
  useEffect(() => {
    if (getTableDataStatus === "completed" && !getTableDataError && getTableDataResult) {
      setData(getTableDataResult);
      setSelectedIds(getTableDataResult.map((item: ITableRowData) => item.id));
    }
  }, [getTableDataStatus]);

  useEffect(() => {
    if (postRelatedEntityDataStatus === "completed" && !postRelatedEntityDataError && postRelatedEntityDataResult) {
      setData(postRelatedEntityDataResult);
      setShowNewModal(false);
    }
  }, [postRelatedEntityDataStatus]);

  const handleOnNewClicked = () => {
    if (previewMode) {
      return;
    }
    setShowNewModal(true);
  };

  const handleOnRowClick = () => {
    if (previewMode) {
      return;
    }
    setShowNewModal(true);
  };

  const handleOnSelectionChange = (selectedIds: string[]) => {
    setSelectedIds(selectedIds);
  };

  const handleOnModalSubmit = () => {
    const newData = data.filter(item => selectedIds.includes(item.related_entity_id));
    selectedIds.forEach(id => {
      if (!newData.find(item => item.related_entity_id === id)) {
        newData.push({id: createUUID(), related_entity_id: id, related_entity_type_id: widget.datamodelId});
      }
    });
    
    postRelatedEntityData(`alertcase/${params.id}/${widget.datamodelId}/related_entities?order_by=${sortedByCol.column} ${sortedByCol.direction}`, {
      id: params.id,
      data: newData.map(item => ({
        id: item.id,
        related_entity_id: item.related_entity_id,
        related_entity_type_id: item.related_entity_type_id
      })),
      rowData: []
    });
  };

  const renderModalBody = () => {
    return (
      <div className="relatedEntityWidget-modalBody">
        {validateLoadingStatusNew([getTableDataStatus, postRelatedEntityDataStatus]) && <Spinner />}
        <ReferenceWidget 
          previewMode={previewMode} 
          selectedIds={selectedIds} 
          selectMultiple={true}
          widget={{
            id: widget.id,
            label: widget.label,
            type: "reference",
            modalType: "field",
            selectedColumns: widget.selectedColumns,
            modalData: widget.modalData,
            required: false,
            readonly: false,
            reference_id: widget.datamodelId
          }}
          onChange={handleOnSelectionChange}
        />
      </div>
    );
  };

  return <div className="relatedEntityWidget">
    {validateLoadingStatusNew([getTableDataStatus, postRelatedEntityDataStatus]) && <Spinner />}

    <ControlledTable
      readOnly={isUserReadOnly}
      title={`Related entities (${widget.label})`}
      data={data}
      selectionMode="off"
      columns={widget.selectedColumns}
      onSorted={setSortedByCol}
      onNewClicked={handleOnNewClicked}
      onScroll={noop}
      onRowClick={handleOnRowClick}
      style="compact"
    />
    <SimpleModal
      mode={`Link related entity (${widget.label})`}
      onModalClickHandler={noop}
      modalBody={renderModalBody()}
      buttonText="Save"
      handleModalClosed={() => {
        setShowNewModal(false);
        setSelectedIds(getTableDataResult.map((item: ITableRowData) => item.id));
      }}
      handleSubmit={handleOnModalSubmit}
      show={showNewModal}
    />
  </div>;
};

export default RelatedEntityWidget;
